import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';

export type WedgeBriefLogLook = 'brieflog-relog' | 'brieflog-outcome' | 'brieflog-chip' | 'brieflog-dictate' | 'brieflog-save';
export type WedgeBriefLogAreaVariant = 'wedge-brief-log-area';

const BRIEF_LOG_LOOKS = new Set<string>(['brieflog-relog', 'brieflog-outcome', 'brieflog-chip', 'brieflog-dictate', 'brieflog-save']);
export const isWedgeBriefLogLook = (look: string): look is WedgeBriefLogLook => BRIEF_LOG_LOOKS.has(look);
export const isWedgeBriefLogAreaVariant = (variant: unknown): variant is WedgeBriefLogAreaVariant => variant === 'wedge-brief-log-area';

/* Released controls of the typed brief log tab (prep-brief-v2-tabs BriefV2Log), off-scale. The tab paints from its own two-theme palette, so
   a look holds the geometry and the type only: the caller passes the fill, the border, the ink, the font family, the held state of Save
   (cursor, pointer events, shadow) and the fill that follows the pick. None sets padding where the source leaves the browser default, so the
   box matches the released one. Every look is 44px tall at least (the outcome tile 92px), so no look here draws a transparent target
   (wedgeBriefLogDrawsHit). The re-log button is clear of any fill; the outcome tile is a column of glyph and label; the come-back chip is a
   44px pill with 4px side padding that never wraps; the dictate button is a row with an icon; Save takes the free width and has no border. */
export const wedgeBriefLogDrawsHit = (look: string): boolean => !isWedgeBriefLogLook(look);

export function wedgeBriefLogStyle(look: WedgeBriefLogLook): CSSProperties {
  switch (look) {
    case 'brieflog-relog':
      return {minHeight: 44, background: 'transparent', borderRadius: radius.lg, fontSize: 14, fontWeight: weight.semibold, cursor: 'pointer'};
    case 'brieflog-outcome':
      return {
        minHeight: 92, borderRadius: radius.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        cursor: 'pointer', boxSizing: 'border-box',
      };
    case 'brieflog-chip':
      return {minWidth: 0, padding: '0 4px', whiteSpace: 'nowrap', minHeight: 44, borderRadius: radius.pill, fontSize: 13, fontWeight: weight.semibold, cursor: 'pointer'};
    case 'brieflog-dictate':
      return {
        minHeight: 44, padding: '0 16px', borderRadius: radius.lg, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14,
        fontWeight: weight.semibold, cursor: 'pointer',
      };
    default:
      return {flex: 1, minHeight: 44, borderRadius: radius.lg, border: 0, fontSize: 15, fontWeight: weight.semibold};
  }
}

/* The note field of the brief log tab: one bare native textarea, a block 96px tall at full width with 8px above, no resize handle, 14px of
   padding, a 14px radius and 15px type, border-box. The caller passes the border, the fill, the ink and the font family. */
export function wedgeBriefLogAreaStyle(_variant: WedgeBriefLogAreaVariant): CSSProperties {
  return {display: 'block', width: '100%', marginTop: 8, height: 96, resize: 'none', padding: 14, borderRadius: radius.xl, fontSize: 15, boxSizing: 'border-box'};
}
